// src/pages/PerformanceDashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip as MuiTooltip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  Speed,
  ErrorOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import { performanceStore } from '../utils/performanceMetricsStore';
import { getFriendlyEndpoint } from '../utils/endpointLabels';
import {
  performanceAPI,
  PerformanceMetric,
  webVitalsAPI,
  WebVitalMetric,
  WebVitalStats,
} from '../services/api';
import { useTabQueryParam } from '../hooks/useTabQueryParam';
import PageTitle from '../components/common/PageTitle';
import PerformanceDashboardSubMenu, {
  PERFORMANCE_TAB_IDS,
  type PerformanceTabId,
} from '../components/menus/PerformanceDashboardSubMenu';

interface TimelineDataPoint {
  index: number;
  timeLabel: string;
  fullTime: string;
  responseTime: number;
  endpoint: string;
}

interface EndpointDataPoint {
  endpoint: string;
  rawEndpoint: string;
  avgTime: number;
  count: number;
}

interface PercentageDataPoint {
  name: string;
  value: number;
  color: string;
}

interface WebVitalTrendPoint {
  index: number;
  timeLabel: string;
  value: number;
}

interface WebVitalDistributionPoint {
  metric: string;
  good: number;
  needsImprovement: number;
  poor: number;
}

type DateRange = 'today' | '7days' | '30days' | 'all';

const FAST_THRESHOLD_MS = 200;
const SLOW_THRESHOLD_MS = 500;

// The five Core Web Vitals, in the order they're always displayed.
const WEB_VITAL_METRIC_ORDER = ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'] as const;

const WEB_VITAL_LABELS: Record<string, string> = {
  LCP: 'Largest Contentful Paint',
  INP: 'Interaction to Next Paint',
  CLS: 'Cumulative Layout Shift',
  FCP: 'First Contentful Paint',
  TTFB: 'Time to First Byte',
};

// Google's official Core Web Vitals thresholds. Mirrors the same table in
// WebVitalService.cs - duplicated rather than fetched, since these are
// stable published constants (not data that changes per-deployment), and
// are only used here to draw reference lines on the trend charts. The
// per-sample and p75 ratings themselves always come from the backend
// (which in turn just relays whatever the web-vitals library computed
// client-side) - this app never re-derives a rating from these numbers.
const WEB_VITAL_THRESHOLDS: Record<
  string,
  { good: number; needsImprovement: number }
> = {
  LCP: { good: 2500, needsImprovement: 4000 },
  INP: { good: 200, needsImprovement: 500 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};

const getRatingColor = (rating: string | undefined): string => {
  if (rating === 'good') return '#4caf50';
  if (rating === 'needs-improvement') return '#ff9800';
  if (rating === 'poor') return '#f44336';
  return '#9e9e9e';
};

const getRatingLabel = (rating: string | undefined): string => {
  if (rating === 'good') return 'Good';
  if (rating === 'needs-improvement') return 'Needs improvement';
  if (rating === 'poor') return 'Poor';
  return 'Unknown';
};

const formatWebVitalValue = (metricName: string, value: number): string =>
  metricName === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`;

// Computes the fromDate bound for a given DateRange selection. Shared by
// both the backend and frontend (web vitals) fetches so "Last 7 Days" etc.
// means the same window on both tabs. `now` is a parameter (rather than
// read internally) so callers can pass a single shared Date for a fetch
// pass, keeping fromDate/toDate consistent with each other.
const getDateRangeBounds = (
  range: DateRange,
  now: Date
): { fromDate: Date | undefined; toDate: Date } => {
  switch (range) {
    case 'today':
      return {
        fromDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        toDate: now,
      };
    case '7days':
      return {
        fromDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        toDate: now,
      };
    case '30days':
      return {
        fromDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        toDate: now,
      };
    default:
      return { fromDate: undefined, toDate: now };
  }
};

const PerformanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tabId, setTabId] = useTabQueryParam<PerformanceTabId>(
    'tab',
    PERFORMANCE_TAB_IDS,
    'backend'
  );
  const [realTimeMetrics, setRealTimeMetrics] = useState(
    performanceStore.getMetrics()
  );
  const [historicalMetrics, setHistoricalMetrics] = useState<
    PerformanceMetric[]
  >([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [loading, setLoading] = useState(false);

  const [webVitalStats, setWebVitalStats] = useState<WebVitalStats | null>(
    null
  );
  const [webVitalMetrics, setWebVitalMetrics] = useState<WebVitalMetric[]>([]);
  const [webVitalsLoading, setWebVitalsLoading] = useState(false);

  // Real-time updates. Both the push-based subscription and the periodic
  // poll are gated on autoRefresh, so toggling it off genuinely freezes the
  // dashboard at its last values (previously the subscription alone kept
  // pushing updates regardless of the toggle, making the 2-second interval
  // this effect used to run separately redundant - merged here since they
  // now share the same autoRefresh dependency).
  useEffect(() => {
    const updateMetrics = () => {
      setRealTimeMetrics(performanceStore.getMetrics());
    };

    updateMetrics();

    if (!autoRefresh) return;

    const unsubscribe = performanceStore.subscribe(updateMetrics);
    const interval = setInterval(() => {
      setRealTimeMetrics([...performanceStore.getMetrics()]);
    }, 2000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [autoRefresh]);

  // Historical data fetching
  useEffect(() => {
    fetchHistoricalData(dateRange);
  }, [dateRange]);

  // Web vitals are only fetched once the Frontend tab is actually opened -
  // most sessions will only ever look at Backend, so there's no reason to
  // fire this on every mount. Once opened, it stays in sync with the same
  // shared dateRange the Backend tab uses.
  useEffect(() => {
    if (tabId === 'frontend') {
      fetchWebVitalsData(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, dateRange]);

  const fetchHistoricalData = async (range: DateRange) => {
    setLoading(true);
    try {
      const now = new Date();
      const { fromDate, toDate } = getDateRangeBounds(range, now);

      const response = await performanceAPI.getMetrics(
        1,
        1000,
        undefined,
        fromDate,
        toDate
      );
      setHistoricalMetrics(response.data);
    } catch (error) {
      console.error('Error fetching historical data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebVitalsData = async (range: DateRange) => {
    setWebVitalsLoading(true);
    try {
      const now = new Date();
      const { fromDate, toDate } = getDateRangeBounds(range, now);

      const [statsResponse, metricsResponse] = await Promise.all([
        webVitalsAPI.getStats(fromDate, toDate),
        webVitalsAPI.getMetrics(1, 1000, undefined, fromDate, toDate),
      ]);
      setWebVitalStats(statsResponse.data);
      setWebVitalMetrics(metricsResponse.data);
    } catch (error) {
      console.error('Error fetching web vitals data:', error);
    } finally {
      setWebVitalsLoading(false);
    }
  };

  // Combine real-time + historical for display
  const allMetrics = [...historicalMetrics, ...realTimeMetrics];
  const realTimeStats = performanceStore.getStats();

  const overallAvgMs = allMetrics.length
    ? Math.round(
        allMetrics.reduce((sum, m) => sum + m.responseTimeMs, 0) /
          allMetrics.length
      )
    : 0;
  const slowCount = allMetrics.filter(
    (m) => m.responseTimeMs > SLOW_THRESHOLD_MS
  ).length;

  const getTimelineData = (): TimelineDataPoint[] => {
    return allMetrics.slice(-100).map((m, idx) => {
      const ts = new Date(m.timestamp);
      return {
        index: idx,
        timeLabel: ts.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        fullTime: ts.toLocaleString(),
        responseTime: m.responseTimeMs,
        endpoint: getFriendlyEndpoint(m.endpoint),
      };
    });
  };

  const getEndpointData = (): EndpointDataPoint[] => {
    const endpointMap = new Map<string, { total: number; count: number }>();

    allMetrics.forEach((m) => {
      const key = getFriendlyEndpoint(m.endpoint);
      const existing = endpointMap.get(key) || { total: 0, count: 0 };
      endpointMap.set(key, {
        total: existing.total + m.responseTimeMs,
        count: existing.count + 1,
      });
    });

    return Array.from(endpointMap.entries())
      .map(([endpoint, data]) => ({
        endpoint,
        rawEndpoint: endpoint,
        avgTime: Math.round(data.total / data.count),
        count: data.count,
      }))
      .sort((a, b) => b.avgTime - a.avgTime)
      .slice(0, 8);
  };

  const getStatusColor = (): string => {
    if (realTimeStats.averageResponseTime < FAST_THRESHOLD_MS) return '#4caf50';
    if (realTimeStats.averageResponseTime < SLOW_THRESHOLD_MS) return '#ff9800';
    return '#f44336';
  };

  const getStatusLabel = (): string => {
    if (realTimeStats.averageResponseTime < FAST_THRESHOLD_MS)
      return 'Excellent';
    if (realTimeStats.averageResponseTime < SLOW_THRESHOLD_MS) return 'Good';
    return 'Slow';
  };

  const getPercentageData = (): PercentageDataPoint[] => {
    const good = allMetrics.filter(
      (m) => m.responseTimeMs < FAST_THRESHOLD_MS
    ).length;
    const fair = allMetrics.filter(
      (m) =>
        m.responseTimeMs >= FAST_THRESHOLD_MS &&
        m.responseTimeMs < SLOW_THRESHOLD_MS
    ).length;
    const slow = allMetrics.filter(
      (m) => m.responseTimeMs >= SLOW_THRESHOLD_MS
    ).length;

    return [
      { name: 'Fast (<200ms)', value: good, color: '#4caf50' },
      { name: 'Fair (200-500ms)', value: fair, color: '#ff9800' },
      { name: 'Slow (>500ms)', value: slow, color: '#f44336' },
    ];
  };

  const getHealthSummary = (): {
    severity: 'success' | 'info' | 'warning';
    title: string;
    message: string;
  } => {
    if (allMetrics.length === 0) {
      return {
        severity: 'info',
        title: 'No activity yet',
        message:
          'Once people start using the system, performance information will appear here.',
      };
    }

    const slowPct = (slowCount / allMetrics.length) * 100;

    if (overallAvgMs < FAST_THRESHOLD_MS && slowPct < 5) {
      return {
        severity: 'success',
        title: 'Your system is running smoothly',
        message: `Actions are completing in ${overallAvgMs}ms on average — well within the "feels instant" range. ${
          slowCount === 0
            ? 'No slow actions detected.'
            : `Only ${slowCount} of ${allMetrics.length} actions were slow.`
        }`,
      };
    }

    if (overallAvgMs < SLOW_THRESHOLD_MS && slowPct < 15) {
      return {
        severity: 'info',
        title: 'Your system is responsive',
        message: `Actions are averaging ${overallAvgMs}ms — fine for everyday use, though some are noticeably slower. ${slowCount} of ${allMetrics.length} actions took longer than half a second.`,
      };
    }

    return {
      severity: 'warning',
      title: 'Your system is running slowly',
      message: `Actions are averaging ${overallAvgMs}ms with ${slowCount} of ${allMetrics.length} taking longer than half a second. Check the "Slowest actions" chart below to see which ones are causing the delay.`,
    };
  };

  const getWebVitalsHealthSummary = (): {
    severity: 'success' | 'info' | 'warning';
    title: string;
    message: string;
  } => {
    const summaries = webVitalStats ? Object.values(webVitalStats.metrics) : [];

    if (summaries.length === 0) {
      return {
        severity: 'info',
        title: 'No frontend data yet',
        message:
          'Once people start using the app, frontend performance information will appear here.',
      };
    }

    const poorCount = summaries.filter((s) => s.p75Rating === 'poor').length;
    const needsImprovementCount = summaries.filter(
      (s) => s.p75Rating === 'needs-improvement'
    ).length;

    if (poorCount === 0 && needsImprovementCount === 0) {
      return {
        severity: 'success',
        title: 'Frontend performance looks good',
        message:
          'All measured metrics are within Google\'s "good" thresholds at the 75th percentile.',
      };
    }

    if (poorCount === 0) {
      return {
        severity: 'info',
        title: 'Frontend performance is mostly good',
        message: `${needsImprovementCount} metric${needsImprovementCount === 1 ? '' : 's'} could use improvement at the 75th percentile.`,
      };
    }

    return {
      severity: 'warning',
      title: 'Some frontend metrics need attention',
      message: `${poorCount} metric${poorCount === 1 ? '' : 's'} rated "poor" at the 75th percentile. Check the trend charts below to see when it started.`,
    };
  };

  const getWebVitalTrendData = (metricName: string): WebVitalTrendPoint[] => {
    return webVitalMetrics
      .filter((m) => m.metricName === metricName)
      .slice()
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      .map((m, idx) => ({
        index: idx,
        timeLabel: new Date(m.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        value: m.value,
      }));
  };

  const getWebVitalDistributionData = (): WebVitalDistributionPoint[] =>
    WEB_VITAL_METRIC_ORDER.map((metric) => {
      const summary = webVitalStats?.metrics[metric];
      return {
        metric,
        good: summary?.goodCount ?? 0,
        needsImprovement: summary?.needsImprovementCount ?? 0,
        poor: summary?.poorCount ?? 0,
      };
    });

  const timelineData = getTimelineData();
  const endpointData = getEndpointData();
  const percentageData = getPercentageData();
  const health = getHealthSummary();
  const webVitalsHealth = getWebVitalsHealthSummary();
  const webVitalDistributionData = getWebVitalDistributionData();
  const hasAnyWebVitalSamples =
    Object.keys(webVitalStats?.metrics ?? {}).length > 0;

  const renderSpeedScaleKey = () => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{
        mb: 3,
        p: 1.5,
        backgroundColor: 'action.hover',
        borderRadius: 1,
        alignItems: { xs: 'flex-start', sm: 'center' },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Speed scale:
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#4caf50',
          }}
        />
        <Typography variant="body2">Under 200ms — feels instant</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#ff9800',
          }}
        />
        <Typography variant="body2">
          200–500ms — fine for everyday use
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#f44336',
          }}
        />
        <Typography variant="body2">Over 500ms — noticeably slow</Typography>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <PageTitle
        title="Performance Dashboard"
        showSeparator={false}
        modeText=""
        leftContent={
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Go back"
            sx={{ color: '#fff', ml: { xs: 0.5, sm: 1.5 } }}
          >
            <ArrowBackIcon />
          </IconButton>
        }
        rightContent={
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              pr: { xs: 1, sm: 3 },
            }}
          >
            <Chip
              label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              variant="outlined"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255, 255, 255, 0.7)',
                ...(autoRefresh && {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setRealTimeMetrics([...performanceStore.getMetrics()]);
                fetchHistoricalData(dateRange);
                if (tabId === 'frontend') fetchWebVitalsData(dateRange);
              }}
              sx={{ color: '#fff', borderColor: 'rgba(255, 255, 255, 0.7)' }}
            >
              Refresh
            </Button>
          </Box>
        }
      />

      <PerformanceDashboardSubMenu
        selectedTab={tabId}
        setSelectedTab={setTabId}
        buttonBottomBorder={false}
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Date Range Filter - shared between both tabs */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Time Range:
          </Typography>
          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={(e, value) => {
              if (value) setDateRange(value as DateRange);
            }}
            size="small"
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="7days">Last 7 Days</ToggleButton>
            <ToggleButton value="30days">Last 30 Days</ToggleButton>
            <ToggleButton value="all">All Time</ToggleButton>
          </ToggleButtonGroup>
          {tabId === 'backend' && loading && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
          {tabId === 'frontend' && webVitalsLoading && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
        </Box>

        {tabId === 'backend' && (
          <>
            {/* Plain-English health summary */}
            <Alert severity={health.severity} sx={{ mb: 3 }}>
              <AlertTitle>{health.title}</AlertTitle>
              {health.message}
            </Alert>

            {/* Speed scale legend */}
            {renderSpeedScaleKey()}

            <Grid container spacing={3}>
              {/* Status Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 110,
                          height: 110,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(),
                          margin: '0 auto 16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '2rem',
                          fontWeight: 700,
                        }}
                      >
                        {realTimeStats.averageResponseTime}ms
                      </Box>
                      <Typography variant="h6">Right now</Typography>
                      <Chip label={getStatusLabel()} sx={{ mt: 1 }} />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Average speed of the most recent actions
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Avg Response Time */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Average speed
                        </Typography>
                        <Typography variant="h4">{overallAvgMs}ms</Typography>
                      </Box>
                      <Speed sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Average time to complete an action over the selected
                      period
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Requests */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Total actions
                        </Typography>
                        <Typography variant="h4">
                          {allMetrics.length}
                        </Typography>
                      </Box>
                      <TrendingUp
                        sx={{ fontSize: 40, color: 'success.main' }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Pages opened, saves, lookups, etc. over the selected
                      period
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Slow Requests */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Slow actions
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            color:
                              slowCount > 0 ? 'error.main' : 'success.main',
                          }}
                        >
                          {slowCount}
                        </Typography>
                      </Box>
                      <ErrorOutline
                        sx={{
                          fontSize: 40,
                          color: slowCount > 0 ? 'error.main' : 'success.main',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Actions that took longer than half a second to complete
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timeline Chart */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Speed over time
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Each point is one action. Lower is faster.
                  </Typography>
                  {timelineData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timeLabel"
                          interval="preserveStartEnd"
                          minTickGap={30}
                        />
                        <YAxis
                          label={{
                            value: 'milliseconds',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                        <Tooltip
                          formatter={(value: any) => [
                            `${value}ms`,
                            'Response time',
                          ]}
                          labelFormatter={(_label, payload) => {
                            const point = payload?.[0]?.payload as
                              | TimelineDataPoint
                              | undefined;
                            return point
                              ? `${point.endpoint} — ${point.fullTime}`
                              : '';
                          }}
                        />
                        <ReferenceLine
                          y={FAST_THRESHOLD_MS}
                          stroke="#4caf50"
                          strokeDasharray="3 3"
                          label={{
                            value: 'Fast',
                            position: 'right',
                            fill: '#4caf50',
                          }}
                        />
                        <ReferenceLine
                          y={SLOW_THRESHOLD_MS}
                          stroke="#f44336"
                          strokeDasharray="3 3"
                          label={{
                            value: 'Slow',
                            position: 'right',
                            fill: '#f44336',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="responseTime"
                          stroke="#8884d8"
                          dot={false}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Percentage Distribution */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Speed breakdown
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    How many actions fell into each speed range.
                  </Typography>
                  {percentageData.some((d) => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={percentageData}
                          cx="50%"
                          cy="45%"
                          labelLine={false}
                          label={(props: any) => `${props.value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {percentageData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: any, name: any) => [
                            `${value} action${value === 1 ? '' : 's'}`,
                            name,
                          ]}
                        />
                        <Legend
                          verticalAlign="bottom"
                          iconType="circle"
                          wrapperStyle={{ fontSize: '0.85rem' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Endpoint Breakdown */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Slowest actions
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    The eight actions with the highest average response time.
                    Bars above the red line are noticeably slow.
                  </Typography>
                  {endpointData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={340}>
                      <BarChart
                        data={endpointData}
                        margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="endpoint"
                          angle={-25}
                          textAnchor="end"
                          height={90}
                          interval={0}
                        />
                        <YAxis
                          label={{
                            value: 'milliseconds',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                        <Tooltip
                          formatter={(value: any, _name: any, item: any) => {
                            const count = item?.payload?.count;
                            return [
                              `${value}ms (avg of ${count} action${count === 1 ? '' : 's'})`,
                              'Response time',
                            ];
                          }}
                        />
                        <ReferenceLine
                          y={FAST_THRESHOLD_MS}
                          stroke="#4caf50"
                          strokeDasharray="3 3"
                          label={{
                            value: 'Fast',
                            position: 'right',
                            fill: '#4caf50',
                          }}
                        />
                        <ReferenceLine
                          y={SLOW_THRESHOLD_MS}
                          stroke="#f44336"
                          strokeDasharray="3 3"
                          label={{
                            value: 'Slow',
                            position: 'right',
                            fill: '#f44336',
                          }}
                        />
                        <Bar
                          dataKey="avgTime"
                          fill="#8884d8"
                          name="Avg response time"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Recent Requests Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, pb: 0 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Recent activity
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    The 20 most recent actions. Hover an action name to see the
                    technical path.
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Speed
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Result
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Time
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allMetrics
                          .slice()
                          .reverse()
                          .slice(0, 20)
                          .map((metric, idx) => {
                            const friendly = getFriendlyEndpoint(
                              metric.endpoint
                            );
                            const isOk =
                              metric.statusCode != null &&
                              metric.statusCode < 400;
                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <MuiTooltip title={metric.endpoint} arrow>
                                    <Typography variant="body2">
                                      {friendly}
                                    </Typography>
                                  </MuiTooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={`${metric.responseTimeMs}ms`}
                                    color={
                                      metric.responseTimeMs < FAST_THRESHOLD_MS
                                        ? 'success'
                                        : metric.responseTimeMs <
                                            SLOW_THRESHOLD_MS
                                          ? 'warning'
                                          : 'error'
                                    }
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <MuiTooltip
                                    title={`HTTP ${metric.statusCode ?? 'unknown'}`}
                                    arrow
                                  >
                                    <Chip
                                      label={isOk ? 'OK' : 'Failed'}
                                      color={isOk ? 'success' : 'error'}
                                      variant="outlined"
                                      size="small"
                                    />
                                  </MuiTooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption">
                                    {new Date(
                                      metric.timestamp
                                    ).toLocaleTimeString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {tabId === 'frontend' && (
          <>
            {/* Plain-English health summary */}
            <Alert severity={webVitalsHealth.severity} sx={{ mb: 3 }}>
              <AlertTitle>{webVitalsHealth.title}</AlertTitle>
              {webVitalsHealth.message}
            </Alert>

            <Grid container spacing={3}>
              {/* Stat cards - one per Core Web Vital */}
              {WEB_VITAL_METRIC_ORDER.map((metric) => {
                const summary = webVitalStats?.metrics[metric];
                return (
                  <Grid item xs={12} sm={6} md={4} key={metric}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          {WEB_VITAL_LABELS[metric]} ({metric})
                        </Typography>
                        {summary ? (
                          <>
                            <Typography variant="h4">
                              {formatWebVitalValue(metric, summary.p75Value)}
                            </Typography>
                            <Chip
                              label={getRatingLabel(summary.p75Rating)}
                              size="small"
                              sx={{
                                mt: 1,
                                backgroundColor: getRatingColor(
                                  summary.p75Rating
                                ),
                                color: 'white',
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: 'block', mt: 1 }}
                            >
                              75th percentile of {summary.sampleCount} sample
                              {summary.sampleCount === 1 ? '' : 's'}
                            </Typography>
                          </>
                        ) : (
                          <Typography color="textSecondary" variant="body2">
                            No data yet
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}

              {/* Rating Distribution */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Rating distribution
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    How many real-user samples fell into each rating band, per
                    metric.
                  </Typography>
                  {hasAnyWebVitalSamples ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={webVitalDistributionData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="metric" width={60} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="good"
                          stackId="a"
                          fill="#4caf50"
                          name="Good"
                        />
                        <Bar
                          dataKey="needsImprovement"
                          stackId="a"
                          fill="#ff9800"
                          name="Needs improvement"
                        />
                        <Bar
                          dataKey="poor"
                          stackId="a"
                          fill="#f44336"
                          name="Poor"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Trend small-multiples, one chart per metric */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Trends over time
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Each point is one real page load. Dashed lines mark Google's
                  "good" and "poor" thresholds for that metric.
                </Typography>
                <Grid container spacing={2}>
                  {WEB_VITAL_METRIC_ORDER.map((metric) => {
                    const trendData = getWebVitalTrendData(metric);
                    const thresholds = WEB_VITAL_THRESHOLDS[metric];
                    return (
                      <Grid item xs={12} sm={6} md={4} key={metric}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {metric}
                          </Typography>
                          {trendData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={180}>
                              <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="timeLabel"
                                  interval="preserveStartEnd"
                                  minTickGap={30}
                                  tick={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip
                                  formatter={(value: any) => [
                                    formatWebVitalValue(
                                      metric,
                                      value as number
                                    ),
                                    metric,
                                  ]}
                                />
                                <ReferenceLine
                                  y={thresholds.good}
                                  stroke="#4caf50"
                                  strokeDasharray="3 3"
                                />
                                <ReferenceLine
                                  y={thresholds.needsImprovement}
                                  stroke="#f44336"
                                  strokeDasharray="3 3"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="#8884d8"
                                  dot={false}
                                  strokeWidth={2}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <Typography color="textSecondary" variant="body2">
                              No data available
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default PerformanceDashboard;
